import { faFax } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useAxios from '../../../hooks/useAxios';
import useAxiosHook from '../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../hooks/useEndpointGuard';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useTableColumns from '../../../hooks/useTableColumns';
import useTableState from '../../../hooks/useTableState';
import { WithPagination } from '../../../types/api';
import * as sortingMachinesGuard from '../../../utils/constants/auth/sortingMachines';
import * as sortingMapsGuard from '../../../utils/constants/auth/sortingMaps';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { sequential } from '../../../utils/helpers/functions';
import { queryString } from '../../../utils/helpers/http';
import { httpQueryObject } from '../../../utils/helpers/misc';
import Table from '../../DataTable/Table/Table';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import Sidebar from '../../layout/flex/Sidebar';
import Filters from '../Components/Filters/Filters';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import CreateDialog from './Dialogs/CreateDialogSortingMachine/CreateDialogSortingMachine';
import DeleteDialog from './Dialogs/Delete/DeleteDialog';
import ViewDialog from './Dialogs/View/ViewDialog';
import {
  SortingMachinesType,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './SortingMachine.functions';
import useTableFilters from './useTableFilters';

function SortingMachines(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  usePageTitle(t('Sorting Machines'));
  const [action, setAction] = useState<string>('');
  const [caller, setCaller] =
    useState<'group-actions' | 'context-menu'>('context-menu');
  const readGuard = useEndpointGuard(
    sortingMachinesGuard.resourceSortingMachinesRoutes
  );
  const createGuard = useEndpointGuard(
    sortingMachinesGuard.createSortingMachinesRoutes
  );
  const editGuard = useEndpointGuard(
    sortingMachinesGuard.editSortingMachinesRoutes
  );
  const deleteGuard = useEndpointGuard(
    sortingMachinesGuard.deleteSortingMachinesRoutes
  );
  const readGuardMaps = useEndpointGuard(
    sortingMapsGuard.resourceSortingMapsRoutes
  );

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    selectionMultiple,
    setSortField,
    setSortOrder,
    setSelection,
    setSelectionMultiple,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<SortingMachinesType>(tableStorageKey);

  const { filters, headerFiltersCount, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const {
    data,
    error,
    isLoading,
    reload: reloadCollection,
  } = useAxiosHook<WithPagination<SortingMachinesType[]>>(
    '/orders/sorting/machine' + queryString(httpQueryObject(httpFiltersObj))
  );

  const { reload: editReload } = useAxios();

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.SortingMachinesPage,
    constructIdRoute(RoutePaths.ViewSortingMachines, contextMenuSelection?.id),
    readGuard
  );

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(
    RoutePaths.SortingMachinesPage,
    RoutePaths.CreateSortingMachines,
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.SortingMachinesPage,
    constructIdRoute(RoutePaths.EditSortingMachines, contextMenuSelection?.id),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.SortingMachinesPage,
    constructIdRoute(
      RoutePaths.DeleteSortingMachines,
      contextMenuSelection?.id
    ),
    deleteGuard
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'clients',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard,
          readGuardMaps
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard,
          readGuardMaps
        )}
      />,
    ],
    [
      columns,
      t,
      setContextMenuSelection,
      setAction,
      setCaller,
      readGuard,
      editGuard,
      deleteGuard,
      readGuardMaps,
    ]
  );

  const handleRedirectSortingMaps = useCallback(() => {
    if (!contextMenuSelection) {
      return;
    }
    history.push(
      constructIdRoute(RoutePaths.SortingMapsPage, contextMenuSelection.id)
    );
  }, [history, contextMenuSelection]);

  const handleChangeActiveSortingMap = useCallback(() => {
    if (!contextMenuSelection) {
      return;
    }

    editReload({
      url: `/orders/sorting/machine/${contextMenuSelection?.id}`,
      method: 'PUT',
      payload: { sortMapId: contextMenuSelection.sortMapId },
      successCallback: () => {
        reloadCollection();
      },
    });
  }, [editReload, reloadCollection, contextMenuSelection]);

  useEffect(() => {
    if (action && contextMenuSelection) {
      setCaller('context-menu');

      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'active-sorting-map') {
        handleChangeActiveSortingMap();
      }
      if (action === 'sorting-maps') {
        handleRedirectSortingMaps();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
    handleRedirectSortingMaps,
    handleChangeActiveSortingMap,
    setAction,
  ]);

  function handleCreateLogisticsRoute() {
    setContextMenuSelection(undefined);
    showCreateDialog();
  }

  function handelEditButton(data: SortingMachinesType) {
    setContextMenuSelection(data);
    showEditDialog();
  }

  return (
    <div className="page sorting-machines">
      <HeaderPages
        title={t('Sorting Machines')}
        subtitle={t('Sorting machines management')}
        icon={faFax}
      >
        {createGuard && (
          <div>
            <Button
              label={t('Create sorting machine')}
              icon="fas fa-plus"
              className="main-btn"
              onClick={handleCreateLogisticsRoute}
              data-cy="create-btn"
            />
          </div>
        )}
      </HeaderPages>

      <ViewDialog
        isShown={isViewDialogVisible}
        onHide={hideViewDialog}
        isEditBtnShown
        setIsEditDialog={handelEditButton}
      />

      <CreateDialog
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={() => sequential(hideEditDialog, hideCreateDialog)}
        data={contextMenuSelection}
        reloadCollection={reloadCollection}
      />

      <DeleteDialog
        visible={isDeleteDialogVisible}
        data={
          caller === 'context-menu'
            ? contextMenuSelection
              ? [contextMenuSelection]
              : []
            : selectionMultiple
        }
        onHide={() => hideDeleteDialog()}
        reloadSortingMachine={reloadCollection}
        setSelectionMultiple={setSelectionMultiple}
        setContextMenuSelection={setContextMenuSelection}
      />

      <Flex direction="column">
        <Sidebar>
          <Filters
            filters={filters}
            resetAllFilters={resetAllFilters}
            filterHeight={180}
          />
        </Sidebar>
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reloadCollection}
            headerTitle=""
            filterHeight={180}
            headerFiltersCount={headerFiltersCount}
            onHeaderFiltersResetAllBtnClick={resetAllFilters}
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            onRowDoubleClick={showViewDialog}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default SortingMachines;
